import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex, HStack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { ChevronDownIcon } from "lucide-react";

import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import {
  TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
  TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
} from "@/components/trade-filter-selects";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { Backtest } from "../../../../types/backtests";
import type { Trade } from "../../../../types/trade";
import { dedupeBacktestsById } from "../../../../services/backtestsApi";
import { useReportsDetailColors } from "../reportsDetailTheme";
import { reportDetailSectionCardProps } from "./ReportsCardChrome";
import {
  readReportDetailUiState,
  writeReportDetailUiState,
} from "../../../../utils/reportDetailUiPersistence";
import {
  assignStickyReportSimColors,
  buildBacktestTradingPnlSeries,
  buildBaselineTradingPnlSeries,
  clampTradingPnlSeriesToAsOf,
  colorForReportSimIndex,
  filterTradesExitedOnOrBefore,
  MAX_REPORT_PNL_LAYERED_SIMS,
  tradingPnlSeriesHasData,
  type ReportPnlBrushWindow,
  type TradingPnlSeries,
} from "../../../../utils/reportTradingPnlSeries";
import { PortfolioGrowthPnlComparisonChart } from "../../Dashboard/components/PortfolioGrowthChart";

export type ReportTradingPnlSectionProps = {
  reportId?: string | null;
  trades: Trade[];
  linkedBacktests: Backtest[];
  isTradeManagement: boolean;
  tradesLoading?: boolean;
  /** Inclusive end of report window (typically end of report created day). */
  asOfEndMs?: number | null;
  /** Optional short label for the as-of day shown in the subtitle. */
  asOfLabel?: string | null;
};

function ReportTradingPnlLegend({
  series,
}: {
  series: TradingPnlSeries[];
}): React.ReactElement | null {
  if (series.length === 0) return null;

  return (
    <Wrap
      spacing={3}
      rowGap={2}
      mt={3}
      w="100%"
      justify="center"
      data-report-trading-pnl-legend=""
      aria-label="Trading PnL series legend"
    >
      {series.map((s) => (
        <WrapItem key={s.id}>
          <HStack spacing={2} align="center">
            <Box
              w="10px"
              h="10px"
              borderRadius="2px"
              bg={s.color}
              flexShrink={0}
              aria-hidden
            />
            <span
              className={cn(
                "font-[family-name:var(--font-primary)] text-xs font-medium leading-tight",
                "text-gray-500 dark:text-sidebar-muted-foreground"
              )}
            >
              {s.label}
            </span>
          </HStack>
        </WrapItem>
      ))}
    </Wrap>
  );
}

export function ReportTradingPnlSection({
  reportId,
  trades,
  linkedBacktests,
  isTradeManagement,
  tradesLoading = false,
  asOfEndMs = null,
  asOfLabel = null,
}: ReportTradingPnlSectionProps): React.ReactElement {
  const { subtleText } = useReportsDetailColors();
  const tradesForChart = useMemo(
    () => filterTradesExitedOnOrBefore(trades, asOfEndMs),
    [trades, asOfEndMs]
  );
  const [selectedSimIds, setSelectedSimIds] = useState<string[]>(() => {
    const saved = readReportDetailUiState(reportId).tradingPnlSelectedSimIds;
    const valid = new Set(
      linkedBacktests.filter((b) => b.result != null).map((b) => b.id)
    );
    return saved.filter((id) => valid.has(id));
  });
  const [brushWindow, setBrushWindow] = useState<ReportPnlBrushWindow | null>(
    () => readReportDetailUiState(reportId).tradingPnlBrushWindow
  );
  const [simColorsById, setSimColorsById] = useState<Record<string, string>>(
    () => readReportDetailUiState(reportId).tradingPnlSimColors
  );

  useEffect(() => {
    const valid = new Set(
      linkedBacktests.filter((b) => b.result != null).map((b) => b.id)
    );
    setSelectedSimIds((prev) => {
      const next = prev.filter((id) => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [linkedBacktests]);

  useEffect(() => {
    const ids = dedupeBacktestsById(linkedBacktests)
      .filter((b) => b.result != null)
      .map((b) => b.id);
    setSimColorsById((prev) => {
      const assigned = assignStickyReportSimColors(ids, prev);
      // Keep remembered colors for sims that left the list (same session / refresh).
      const merged = { ...prev, ...assigned };
      const prevKeys = Object.keys(prev);
      if (
        prevKeys.length === Object.keys(merged).length &&
        prevKeys.every((id) => prev[id] === merged[id])
      ) {
        return prev;
      }
      return merged;
    });
  }, [linkedBacktests]);

  useEffect(() => {
    const id = String(reportId ?? "").trim();
    if (!id) return;
    writeReportDetailUiState(id, {
      tradingPnlSelectedSimIds: selectedSimIds,
      tradingPnlBrushWindow: brushWindow,
      tradingPnlSimColors: simColorsById,
    });
  }, [reportId, selectedSimIds, brushWindow, simColorsById]);

  const simOptions = useMemo(
    () =>
      dedupeBacktestsById(linkedBacktests)
        .filter((b) => b.result != null)
        .map((b, index) => ({
          id: b.id,
          label: b.title?.trim() || `Simulator ${b.id.slice(0, 8)}…`,
          color: simColorsById[b.id] ?? colorForReportSimIndex(index),
        })),
    [linkedBacktests, simColorsById]
  );

  const simColorById = useMemo(
    () => new Map(simOptions.map((opt) => [opt.id, opt.color])),
    [simOptions]
  );

  const simMenuOptions = useMemo(
    () =>
      simOptions.map((opt) => ({
        value: opt.id,
        label: opt.label,
        disabled:
          selectedSimIds.length >= MAX_REPORT_PNL_LAYERED_SIMS &&
          !selectedSimIds.includes(opt.id),
      })),
    [simOptions, selectedSimIds]
  );

  const handleSimSelectionChange = useCallback((values: string[]) => {
    const ordered = simOptions
      .filter((opt) => values.includes(opt.id))
      .map((opt) => opt.id);
    setSelectedSimIds(ordered.slice(0, MAX_REPORT_PNL_LAYERED_SIMS));
  }, [simOptions]);

  const toggleSimSelection = useCallback(
    (simId: string, checked: boolean) => {
      if (checked) {
        if (selectedSimIds.includes(simId)) return;
        if (selectedSimIds.length >= MAX_REPORT_PNL_LAYERED_SIMS) return;
        handleSimSelectionChange([...selectedSimIds, simId]);
        return;
      }
      handleSimSelectionChange(selectedSimIds.filter((id) => id !== simId));
    },
    [handleSimSelectionChange, selectedSimIds]
  );

  const layerTriggerLabel =
    selectedSimIds.length === 0
      ? "Layer simulations"
      : selectedSimIds.length === 1
        ? "1 simulation"
        : `${selectedSimIds.length} simulations`;

  const chartSeries = useMemo(() => {
    const baseline = buildBaselineTradingPnlSeries(
      tradesForChart,
      linkedBacktests,
      isTradeManagement ? "Baseline exits" : "All trades (baseline)"
    );
    const sims = simOptions
      .filter((opt) => selectedSimIds.includes(opt.id))
      .map((opt) => {
        const bt = linkedBacktests.find((b) => b.id === opt.id);
        if (!bt) return null;
        return buildBacktestTradingPnlSeries(
          bt,
          tradesForChart,
          simColorById.get(opt.id) ?? opt.color,
          { treatAsTradeManagement: isTradeManagement }
        );
      })
      .filter((s): s is NonNullable<typeof s> => s != null);
    return [baseline, ...sims].map((s) => clampTradingPnlSeriesToAsOf(s, asOfEndMs));
  }, [
    tradesForChart,
    linkedBacktests,
    selectedSimIds,
    isTradeManagement,
    simOptions,
    simColorById,
    asOfEndMs,
  ]);

  const hasChartData = tradingPnlSeriesHasData(chartSeries);

  const emptyMessage = tradesLoading
    ? "Loading trades for PnL chart…"
    : hasChartData
      ? "No closed trades with PnL in this window."
      : "No closed trades with exit time and PnL found. Trade review may still load overlays from execution-candles; this chart uses report trade rows or saved simulation result trades.";

  return (
    <Card {...reportDetailSectionCardProps} mt={6} data-report-trading-pnl-section="">
      <CardBody display="flex" flexDirection="column" w="full">
      <Flex
        justify="space-between"
        align="flex-start"
        flexWrap="wrap"
        gap={3}
        mb={4}
      >
        <Box flex="1" minW={0}>
          <Text
            fontSize={{ base: "2xl", md: "3xl" }}
            color="#38BBD1"
            fontWeight="normal"
            fontFamily="var(--font-primary)"
            lineHeight="1.15"
          >
            Trading PnL
          </Text>
          <Text fontSize="sm" color={subtleText} mt={1} fontFamily="var(--font-primary)">
            Cumulative realized PnL by day, baseline plus selected simulations for comparison
            {asOfLabel ? ` (as of ${asOfLabel}).` : "."}
          </Text>
        </Box>

        {simOptions.length > 0 ? (
          <div
            data-field-select=""
            data-trade-log-columns-control=""
            className="w-fit shrink-0 self-start"
          >
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  data-slot="select-trigger"
                  data-size="default"
                  data-trade-log-columns-trigger=""
                  data-report-trading-pnl-layer-trigger=""
                  aria-label="Layer simulations"
                  className={cn(TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS, "min-w-[10rem]")}
                >
                  <span
                    data-slot="select-value"
                    className="dark:text-sidebar-muted-foreground"
                  >
                    {layerTriggerLabel}
                  </span>
                  <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground dark:text-sidebar-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="bottom"
                avoidCollisions={false}
                data-trade-log-columns-menu=""
                data-field-select-menu=""
                data-report-trading-pnl-layer-menu=""
                className="w-auto min-w-40 max-w-[min(90vw,32rem)]"
              >
                {simMenuOptions.map((opt) => (
                  <DropdownMenuCheckboxItem
                    key={opt.value}
                    className={cn(
                      TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
                      "normal-case"
                    )}
                    checked={selectedSimIds.includes(opt.value)}
                    disabled={opt.disabled}
                    onCheckedChange={(value) =>
                      toggleSimSelection(opt.value, Boolean(value))
                    }
                    onSelect={(event) => event.preventDefault()}
                  >
                    {opt.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </Flex>

      {simOptions.length === 0 ? (
        <Text fontSize="sm" color={subtleText} mb={4} fontFamily="var(--font-primary)">
          Save a simulation from the simulator above to compare its PnL curve against baseline.
        </Text>
      ) : null}

      <PortfolioGrowthPnlComparisonChart
        series={chartSeries}
        emptyMessage={emptyMessage}
        brushWindow={brushWindow}
        onBrushWindowChange={setBrushWindow}
      />

      <ReportTradingPnlLegend series={chartSeries} />
      </CardBody>
    </Card>
  );
}
